import type { SerializedState } from "@synapse/engine";

import type { DailyChallengeProgress } from "../types/dailyChallenges";
import { type GameHistoryEntry, toHistoryEntry } from "../utils/gameHistory";
import type { GameReport } from "../utils/gameReportUtils";
import { getLoadedGraphIdentity, getLoadedGraphVersion } from "./dataLoader";
import { getStorageAdapter } from "./storage";
import { Persistence } from "./storage/persistence";
import { listBackups, recoverFromBackup } from "./storage/recovery";
import {
  createDefaultData,
  getTodayStringEST,
  type AriadneRecord,
  type AriadneSection,
  type PersistentGameState,
  type UnifiedAppData,
} from "./unifiedDataSchema";

export { isQuotaError } from "./storage/quota";
export {
  createDefaultData,
  CURRENT_SCHEMA_VERSION,
  migrateData,
  type PersistentGameState,
  type UnifiedAppData,
} from "./unifiedDataSchema";

// Performance optimization: conditional logging
const _DEV_LOG = __DEV__ && false; // Disable for performance

// ============================================================================
// UNIFIED DATA STORE - Single source of truth for all app data. Persistence
// (one record per section and per game) lives in ./storage.
// ============================================================================

// ============================================================================
// UNIFIED DATA STORE CLASS
// ============================================================================

export class UnifiedDataStore {
  private static instance: UnifiedDataStore;
  private data: UnifiedAppData | null = null;
  private isLoaded = false;
  // Debounced save optimization
  private saveTimeout: NodeJS.Timeout | null = null;
  private hasPendingChanges = false;
  // Set when stored data could not be read: never write over it this session.
  private writesBlocked = false;
  private loading: Promise<UnifiedAppData> | null = null;
  private storage: Persistence | null = null;

  // Private constructor for singleton pattern
  private constructor() {
    console.log(
      "🏗️ UnifiedDataStore constructor called - new instance created",
    );
  }

  public static getInstance(): UnifiedDataStore {
    if (!UnifiedDataStore.instance) {
      console.log("🏗️ Creating first UnifiedDataStore instance");
      UnifiedDataStore.instance = new UnifiedDataStore();
    } else {
      console.log("🔄 Returning existing UnifiedDataStore instance");
    }
    return UnifiedDataStore.instance;
  }

  // ============================================================================
  // CORE DATA OPERATIONS
  // ============================================================================

  // Dev-only: verify local persistence by round-tripping several sections and restoring
  public async debugVerifyPersistence(): Promise<Record<string, boolean>> {
    const report: Record<string, boolean> = {};
    const original = JSON.parse(JSON.stringify(await this.getData()));

    // Get a typed reference to current data after ensuring it's loaded
    const currentData = await this.getData();

    try {
      // Stats (totalWins)
      const prevWins = currentData.stats.totalWins;
      currentData.stats.totalWins = prevWins + 1;
      await this.saveData();
      const afterStats = await this.getData();
      report.stats = afterStats.stats.totalWins === prevWins + 1;

      // Collections (ephemeral key)
      const testCollectionId = "__persist_test__";
      currentData.collections[testCollectionId] = {
        collectedWords: ["test"],
        lastUpdated: Date.now(),
      };
      await this.saveData();
      const afterCollections = await this.getData();
      report.collections = !!afterCollections.collections[testCollectionId];

      // CurrentGames (temp slot)
      const dummyGame: PersistentGameState = {
        startWord: "alpha",
        targetWord: "beta",
        currentWord: "alpha",
        playerPath: ["alpha"],
        optimalPath: ["alpha", "beta"],
        suggestedPathFromCurrent: ["alpha", "beta"],
        gameStatus: "playing",
        optimalChoices: [],
        backtrackHistory: [],
        pathDisplayMode: {
          player: true,
          optimal: false,
          suggested: false,
          ai: false,
        },
        startTime: Date.now(),
      };
      await this.saveCurrentGame(dummyGame, "temp");
      const loadedTemp = await this.loadCurrentGame("temp");
      report.currentGames = !!loadedTemp && loadedTemp.startWord === "alpha";
      await this.clearCurrentGame("temp");

      // Achievements progressive counters (ephemeral id)
      const testAchId = "__persist_test__";
      const beforeCount =
        currentData.achievements.progressiveCounters[testAchId] || 0;
      await this.incrementProgressiveAchievement(testAchId);
      const afterAch = await this.getData();
      report.achievements =
        (afterAch.achievements.progressiveCounters[testAchId] || 0) ===
        beforeCount + 1;

      // Restore original data
      this.data = original;
      await this.saveData();
    } catch {
      try {
        this.data = original;
        await this.saveData();
      } catch {}
      report.error = true as unknown as boolean;
    }
    return report;
  }

  /**
   * Load all data from storage (moving the legacy blob into per-record
   * storage on first run). If storage cannot be read, run on what could be
   * read and never write for this session: an unreadable store is not an
   * empty one.
   */
  public async loadData(): Promise<UnifiedAppData> {
    console.log("src/services/UnifiedDataStore.ts: loadData called");
    if (this.isLoaded && this.data) {
      return this.data;
    }
    if (!this.loading) {
      this.loading = this.readStored().finally(() => {
        this.loading = null;
      });
    }
    return this.loading;
  }

  /** Created on first use, so importing the store touches no storage. */
  private get persistence(): Persistence {
    this.storage ??= new Persistence(getStorageAdapter());
    return this.storage;
  }

  private async readStored(): Promise<UnifiedAppData> {
    const { data, writable } = await this.persistence.load();
    if (!writable) return this.runWithoutSaving(data);
    return this.adopt(data);
  }

  /** Use data in memory only; nothing is written until the next launch. */
  private runWithoutSaving(data: UnifiedAppData): UnifiedAppData {
    console.error("🚨 Stored data left untouched; this session will not save");
    this.data = data;
    this.isLoaded = true;
    this.writesBlocked = true;
    return data;
  }

  /** Take loaded data as current and persist it; a failed save keeps it in memory. */
  private async adopt(data: UnifiedAppData): Promise<UnifiedAppData> {
    this.data = data;
    this.isLoaded = true;
    data.user.lastActiveAt = Date.now();
    try {
      await this.saveData();
    } catch (error) {
      console.error("🚨 Could not persist loaded data:", error);
    }
    return data;
  }

  /**
   * Save what changed. Saves are serialized; a full store keeps everything
   * in memory and leaves the changes pending (see Persistence.save).
   */
  public async saveData(): Promise<void> {
    console.log("src/services/UnifiedDataStore.ts: saveData called");
    if (!this.data) return;
    if (this.writesBlocked) {
      this.hasPendingChanges = true;
      return;
    }

    this.data.user.lastActiveAt = Date.now();
    // Cleared first so a change marked while this save runs stays pending.
    this.hasPendingChanges = false;
    try {
      const complete = await this.persistence.save(this.data);
      if (!complete) this.hasPendingChanges = true;
    } catch (error) {
      this.hasPendingChanges = true;
      console.error("Failed to save data:", error);
      throw error;
    }
  }

  /**
   * Save data with debouncing to avoid frequent writes
   * @param immediate - If true, saves immediately without debouncing (default: false)
   * @param debounceMs - Debounce delay in milliseconds (default: 500)
   */
  public async saveDataDebounced(
    immediate = false,
    debounceMs = 500,
  ): Promise<void> {
    this.hasPendingChanges = true;

    // Clear existing timeout
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }

    if (immediate || debounceMs <= 0) {
      // Save immediately for critical operations
      await this.saveData();
      return;
    }

    // Set up debounced save
    this.saveTimeout = setTimeout(async () => {
      if (this.hasPendingChanges) {
        try {
          await this.saveData();
        } catch (error) {
          console.error("Debounced save failed:", error);
          // Retry once after a short delay
          setTimeout(async () => {
            if (this.hasPendingChanges) {
              try {
                await this.saveData();
              } catch (retryError) {
                console.error("Debounced save retry failed:", retryError);
              }
            }
          }, 1000);
        }
      }
      this.saveTimeout = null;
    }, debounceMs);
  }

  /**
   * Force immediate save of any pending changes
   * Should be called when app is backgrounding or closing
   */
  public async flushPendingChanges(): Promise<void> {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }

    if (this.hasPendingChanges) {
      await this.saveData();
    }
  }

  /**
   * Cleanup method to be called when the service is no longer needed
   * Prevents memory leaks from pending timeouts
   */
  public cleanup(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
    this.hasPendingChanges = false;
  }

  /**
   * Get current data (load if not loaded)
   */
  public async getData(): Promise<UnifiedAppData> {
    console.log("src/services/UnifiedDataStore.ts: getData called");
    if (!this.isLoaded || !this.data) {
      const loadedData = await this.loadData();
      console.log("🔍 UnifiedDataStore.getData() - FRESH LOAD:");
      console.log("   Game history count:", loadedData.gameHistory.length);
      return loadedData;
    }
    console.log("🔍 UnifiedDataStore.getData() - FROM CACHE:");
    console.log("   Game history count:", this.data.gameHistory.length);
    return this.data;
  }

  // ============================================================================
  // CONVENIENCE METHODS (Clean API for your existing code)
  // ============================================================================

  /**
   * Game History Operations
   */
  public async addGameToHistory(gameReport: GameReport): Promise<void> {
    const data = await this.getData();

    console.log(`🎮 GAME ADDED TO HISTORY:`);
    console.log(`   Game: ${gameReport.startWord} → ${gameReport.targetWord}`);

    // Uncapped: whole-history pair dedup and lifetime stats need every game.
    data.gameHistory = [toHistoryEntry(gameReport), ...data.gameHistory];

    console.log(`   Total games in history: ${data.gameHistory.length}`);

    data.user.hasPlayedBefore = true; // Mark user as having played before
    await this.saveData();
  }

  /**
   * Get game history (newest first)
   */
  public async getGameHistory(): Promise<GameHistoryEntry[]> {
    const data = await this.getData();
    return data.gameHistory;
  }

  /**
   * Stats Operations
   */
  public async updateStatsOnGameEnd(gameReport: GameReport): Promise<void> {
    const data = await this.getData();
    data.stats.totalGamesPlayed += 1;
    data.stats.cumulativeMoveAccuracySum += gameReport.moveAccuracy || 0;

    if (gameReport.status === "won") {
      data.stats.totalWins += 1;
    } else if (gameReport.status === "given_up") {
      data.stats.totalGaveUps += 1;
    }

    await this.saveData();
  }

  public async getLifetimeStats() {
    const data = await this.getData();
    return data.stats;
  }

  /**
   * Achievement Operations
   */
  public async unlockAchievement(achievementId: string): Promise<boolean> {
    const data = await this.getData();

    if (!data.achievements.unlockedIds.includes(achievementId)) {
      data.achievements.unlockedIds.push(achievementId);
      data.stats.achievementsUnlocked += 1;
      data.achievements.unlockTimestamps[achievementId] = Date.now();
      await this.saveData();
      return true; // New achievement
    }

    return false; // Already unlocked
  }

  public async getUnlockedAchievements(): Promise<string[]> {
    const data = await this.getData();
    return data.achievements.unlockedIds;
  }

  public async getViewedAchievementIds(): Promise<string[]> {
    const data = await this.getData();
    return data.achievements.viewedIds;
  }

  public async markAchievementAsViewed(achievementId: string): Promise<void> {
    const data = await this.getData();
    if (!data.achievements.viewedIds.includes(achievementId)) {
      data.achievements.viewedIds.push(achievementId);
      await this.saveDataDebounced(); // Non-critical, can be debounced
    }
  }

  public async markMultipleAchievementsAsViewed(
    achievementIds: string[],
  ): Promise<void> {
    const data = await this.getData();
    let hasChanges = false;

    for (const achievementId of achievementIds) {
      if (!data.achievements.viewedIds.includes(achievementId)) {
        data.achievements.viewedIds.push(achievementId);
        hasChanges = true;
      }
    }

    if (hasChanges) {
      await this.saveDataDebounced(); // Non-critical, can be debounced
    }
  }

  /**
   * Progressive Achievement Operations
   */
  public async incrementProgressiveAchievement(
    achievementId: string,
  ): Promise<number> {
    const data = await this.getData();

    if (!data.achievements.progressiveCounters[achievementId]) {
      data.achievements.progressiveCounters[achievementId] = 0;
    }

    data.achievements.progressiveCounters[achievementId] += 1;

    // Mark achievement as unlocked if it's the first time
    if (data.achievements.progressiveCounters[achievementId] === 1) {
      await this.unlockAchievement(achievementId);
    }

    await this.saveData();
    return data.achievements.progressiveCounters[achievementId];
  }

  public async getProgressiveAchievementCount(
    achievementId: string,
  ): Promise<number> {
    const data = await this.getData();
    return data.achievements.progressiveCounters[achievementId] || 0;
  }

  public async getProgressiveAchievementCounts(): Promise<
    Record<string, number>
  > {
    const data = await this.getData();
    return data.achievements.progressiveCounters;
  }

  /**
   * Daily Challenge Operations
   */
  public async updateDailyChallengeProgress(
    challengeId: string,
    progress: DailyChallengeProgress,
  ): Promise<void> {
    const data = await this.getData();
    data.dailyChallenges.progress[challengeId] = progress;
    await this.saveData();
  }

  public async getDailyChallengeProgress(): Promise<
    Record<string, DailyChallengeProgress>
  > {
    const data = await this.getData();
    return data.dailyChallenges.progress;
  }

  /**
   * Ariadne (spec §6)
   */
  public async getAriadne(): Promise<AriadneSection> {
    const data = await this.getData();
    return data.ariadne;
  }

  public async setAriadneState(state: SerializedState | null): Promise<void> {
    const data = await this.getData();
    data.ariadne = { ...data.ariadne, state };
    await this.saveData();
  }

  public async setAriadneRecord(record: AriadneRecord): Promise<void> {
    const data = await this.getData();
    data.ariadne = { ...data.ariadne, record };
    await this.saveData();
  }

  public async setAriadneThreadDefault(on: boolean): Promise<void> {
    const data = await this.getData();
    data.ariadne = { ...data.ariadne, show_thread: on };
    await this.saveData();
  }

  public async getRemainingFreeGames(): Promise<number> {
    const data = await this.getData();
    const today = getTodayStringEST();

    // Reset if new day
    if (data.dailyChallenges.lastResetDate !== today) {
      data.dailyChallenges.freeGamesRemaining = 1;
      data.dailyChallenges.lastResetDate = today;
      await this.saveData();
    }

    return data.dailyChallenges.freeGamesRemaining;
  }

  public async consumeFreeGame(): Promise<number> {
    const data = await this.getData();
    data.dailyChallenges.freeGamesRemaining = Math.max(
      0,
      data.dailyChallenges.freeGamesRemaining - 1,
    );
    await this.saveData();
    return data.dailyChallenges.freeGamesRemaining;
  }

  public async setFreeGamesRemaining(count: number): Promise<void> {
    const data = await this.getData();
    data.dailyChallenges.freeGamesRemaining = Math.max(0, count);
    await this.saveData();
  }

  /**
   * User Profile Operations
   */

  public async getPrivacySettings(): Promise<
    NonNullable<UnifiedAppData["user"]["privacy"]>
  > {
    const data = await this.getData();
    // Return default privacy settings if not set
    return (
      data.user.privacy || {
        allowChallengeSharing: true,
        allowStatsSharing: true,
        dataCollection: false,
      }
    );
  }

  public async updatePrivacySettings(
    settings: Partial<NonNullable<UnifiedAppData["user"]["privacy"]>>,
  ): Promise<void> {
    const data = await this.getData();
    const currentPrivacy = await this.getPrivacySettings();
    data.user.privacy = { ...currentPrivacy, ...settings };
    await this.saveData();
  }

  public async isTutorialComplete(): Promise<boolean> {
    const data = await this.getData();
    return data.user.tutorialComplete;
  }

  public async setTutorialComplete(complete: boolean): Promise<void> {
    const data = await this.getData();
    data.user.tutorialComplete = complete;
    data.user.hasPlayedBefore = true;
    await this.saveData();
  }

  /**
   * Game State Persistence
   */
  public async saveCurrentGame(
    gameState: PersistentGameState,
    type: "regular" | "challenge" | "temp" = "regular",
  ): Promise<void> {
    const data = await this.getData();
    const version = getLoadedGraphVersion();
    const vocabHash = getLoadedGraphIdentity()?.vocabHash;
    data.currentGames[type] = {
      ...gameState,
      ...(version ? { graphVersion: version } : {}),
      ...(vocabHash ? { vocabHash } : {}),
    };
    await this.saveData();
  }

  public async loadCurrentGame(
    type: "regular" | "challenge" | "temp" = "regular",
  ): Promise<PersistentGameState | null> {
    const data = await this.getData();
    return data.currentGames[type];
  }

  public async clearCurrentGame(
    type: "regular" | "challenge" | "temp" = "regular",
  ): Promise<void> {
    const data = await this.getData();
    data.currentGames[type] = null;
    await this.saveData();
  }

  /**
   * Word Collections
   */
  public async recordWordForCollection(
    collectionId: string,
    word: string,
  ): Promise<void> {
    const data = await this.getData();

    if (!data.collections[collectionId]) {
      data.collections[collectionId] = {
        collectedWords: [],
        lastUpdated: Date.now(),
      };
    }

    if (!data.collections[collectionId].collectedWords.includes(word)) {
      data.collections[collectionId].collectedWords.push(word);
      data.collections[collectionId].lastUpdated = Date.now();
      await this.saveDataDebounced(); // Collection progress can be debounced
    }
  }

  public async getCollectionProgress(): Promise<
    Record<string, { collectedWords: string[]; lastUpdated: number }>
  > {
    const data = await this.getData();
    return data.collections;
  }

  /**
   * Data Management & Sync
   */
  public async getDataSize(): Promise<number> {
    const data = await this.getData();
    return JSON.stringify(data).length;
  }

  /**
   * Create fresh user data for account switching
   * This method creates completely new default data without any contamination
   * from previous user sessions, preventing data bleeding between accounts.
   */
  public async createFreshUserData(): Promise<UnifiedAppData> {
    console.log("🆕 Creating fresh user data for new account");
    return createDefaultData();
  }

  /**
   * Word Collection Operations
   */
  public async markWordCollectionAsCompleted(
    collectionId: string,
  ): Promise<boolean> {
    const data = await this.getData();

    if (!data.wordCollections.completedIds.includes(collectionId)) {
      data.wordCollections.completedIds.push(collectionId);
      data.wordCollections.completionTimestamps[collectionId] = Date.now();
      await this.saveData();
      return true; // New completion
    }

    return false; // Already completed
  }

  public async getCompletedWordCollections(): Promise<string[]> {
    const data = await this.getData();
    return data.wordCollections.completedIds;
  }

  public async getViewedWordCollections(): Promise<string[]> {
    const data = await this.getData();
    return data.wordCollections.viewedIds;
  }

  public async markWordCollectionAsViewed(collectionId: string): Promise<void> {
    const data = await this.getData();
    if (!data.wordCollections.viewedIds.includes(collectionId)) {
      data.wordCollections.viewedIds.push(collectionId);
      await this.saveData();
    }
  }

  public async markMultipleWordCollectionsAsViewed(
    collectionIds: string[],
  ): Promise<void> {
    const data = await this.getData();
    let hasChanges = false;

    for (const collectionId of collectionIds) {
      if (!data.wordCollections.viewedIds.includes(collectionId)) {
        data.wordCollections.viewedIds.push(collectionId);
        hasChanges = true;
      }
    }

    if (hasChanges) {
      await this.saveData();
    }
  }

  /**
   * Check and update word collection completion status
   * This should be called whenever word collection progress changes
   */
  public async checkAndUpdateWordCollectionCompletion(): Promise<string[]> {
    const data = await this.getData();
    const newlyCompleted: string[] = [];

    // Import word collections to check against
    const { allWordCollections } = await import("../features/wordCollections");

    for (const collection of allWordCollections) {
      const progress = data.collections[collection.id];

      if (progress && collection.words && collection.words.length > 0) {
        const isCompleted =
          progress.collectedWords.length === collection.words.length;

        if (
          isCompleted &&
          !data.wordCollections.completedIds.includes(collection.id)
        ) {
          // Mark as completed
          data.wordCollections.completedIds.push(collection.id);
          data.wordCollections.completionTimestamps[collection.id] = Date.now();
          newlyCompleted.push(collection.id);
        }
      }
    }

    if (newlyCompleted.length > 0) {
      await this.saveData();
    }

    return newlyCompleted;
  }
}
// Create and export the singleton instance
console.log(
  "src/services/UnifiedDataStore.ts: Creating UnifiedDataStore instance",
);
export const unifiedDataStore = UnifiedDataStore.getInstance();

// ============================================================================
// GLOBAL DEBUG UTILITIES (for development)
// ============================================================================

// Expose debug utilities to global scope for easy console access
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  (window as { synapseDebug?: Record<string, unknown> }).synapseDebug = {
    checkFreeGames: () => unifiedDataStore.getRemainingFreeGames(),
    verifyPersistence: () =>
      unifiedDataStore.debugVerifyPersistence().then((r) => {
        console.log("🔎 Persistence report:", r);
        return r;
      }),
    // Backup and recovery utilities (see storage/recovery.ts)
    listBackups: async () => {
      try {
        const backupKeys = await listBackups();
        console.log("📦 Available backups:", backupKeys);
        return backupKeys;
      } catch (error) {
        console.error("Error listing backups:", error);
        return [];
      }
    },
    recoverFromBackup: async (backupKey: string) => {
      try {
        return await recoverFromBackup(backupKey, getStorageAdapter());
      } catch (error) {
        console.error("Error recovering from backup:", error);
        return false;
      }
    },
  };
}
